<template>
    <div class="widget-status-card-sounds space-y-2">
        <div class="main-text-color font-medium">
            {{ t('settings.soundWarning') }}
        </div>
        <Transition name="slide">
            <div
                v-if="hasActiveNotifications"
                class="pt-4 border-t border-ui-lines"
            >
                <VcButton
                    type="outline"
                    color="secondary"
                    icon="bell-off"
                    class="w-full"
                    @click="disableAllNotifications"
                >
                    <span>{{ t('settings.disableAllNotifications') }}</span>
                </VcButton>
            </div>
        </Transition>
        <div class="pt-3">
            <div class="text-sm mb-1">
                {{ t('settings.thresholdDirection') }}
            </div>
            <div class="flex items-center gap-2">
                <VcSwitch
                    v-model="widgetLayoutModel.reverseThreshold"
                />
                <span class="text-sm">
                    {{ widgetLayoutModel.reverseThreshold
                        ? t('widget.config.higherIsBetter')
                        : t('widget.config.lowerIsBetter')
                    }}
                </span>
            </div>
        </div>
        <div>
            <RedesignFormLabel
                :label="t('widget.config.maximumWarningRangeValue')"
                class="mb-2"
            />
            <VcInputNumber
                v-model="widgetLayoutModel.WarningMaxCounter"
                :max="100"
                :min="0"
                :step="1"
            />
        </div>
        <div>
            <RedesignFormLabel
                :label="t('settings.warningColor')"
                class="mb-2"
            />
            <RedesignWidgetColor
                v-model:value="widgetLayoutModel.warningColor"
                :predefine="predefinedColors"
            />
        </div>
        <div>
            <RedesignFormLabel
                :label="t('settings.warningSound')"
                class="mb-2"
            />
            <SoundSelect
                v-model="widgetLayoutModel.soundWarning"
                :placeholder="t('settings.warningSound')"
                :options="warningSoundList"
                class="w-full"
            />
        </div>
        <div class="main-text-color font-medium pt-3">
            {{ t('settings.soundLimit') }}
        </div>
        <div>
            <RedesignFormLabel
                :label="t('widget.config.maximumLimitRangeValue')"
                class="mb-2"
            />
            <VcInputNumber
                v-model="widgetLayoutModel.LimitMaxCounter"
                :max="100"
                :min="0"
                :step="1"
            />
        </div>
        <div>
            <RedesignFormLabel
                :label="t('settings.limitColor')"
                class="mb-2"
            />
            <RedesignWidgetColor
                v-model:value="widgetLayoutModel.limitColor"
                :predefine="predefinedColors"
            />
        </div>
        <div>
            <RedesignFormLabel
                :label="t('settings.limitSound')"
                class="mb-2"
            />
            <SoundSelect
                v-model="widgetLayoutModel.soundLimit"
                :placeholder="t('settings.limitSound')"
                :options="errorSoundList"
                class="w-full"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { predefinedColors } from '@/views/DashboardSettings/LayoutManagement/layout-management'
import RedesignWidgetColor from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetColor.vue'
import SoundSelect from '@/components/Common/SoundSelect.vue'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import { VcButton, VcSwitch, VcInputNumber } from '@voicenter-team/voicenter-ui-plus'
import { WidgetLayout } from '@/types/dashboard'
import { useVModel } from '@vueuse/core'
import { errorSoundList, warningSoundList } from '@/composables/globalData/useEntitiesData'
import { computed } from 'vue'

const { t } = useI18n()

const props = defineProps<{
    modelValue: WidgetLayout
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: WidgetLayout): void
}>()

const widgetLayoutModel = useVModel(props, 'modelValue', emit, {
    eventName: 'update:modelValue',
})

const hasActiveNotifications = computed(() => {
    return (
        (widgetLayoutModel.value.WarningMaxCounter && widgetLayoutModel.value.WarningMaxCounter > 0) ||
        (widgetLayoutModel.value.LimitMaxCounter && widgetLayoutModel.value.LimitMaxCounter > 0)
    )
})
function disableAllNotifications () {
    widgetLayoutModel.value = {
        ...widgetLayoutModel.value,
        WarningMaxCounter: 0,
        LimitMaxCounter: 0,
        warningColor: '',
        limitColor: '',
        soundWarning: '',
        soundLimit: ''
    }
}
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: max-height 0.4s ease, opacity 0.4s ease;
    overflow: hidden;
    max-height: 100px;
}

.slide-enter-from,
.slide-leave-to {
    max-height: 0;
    opacity: 0;
}
</style>
